import dayjs, { type Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek.js";

dayjs.extend(isoWeek);

export interface BauleiterProjectPeriod {
  id: number;
  name: string;
  number: string;
  plannedStart: string;
  plannedEnd: string;
}

export interface BauleiterCalendarRow {
  id: number;
  name: string;
  type: "BAULEITER";
  projects: BauleiterProjectPeriod[];
}

export function mergeBauleiterRows(current: BauleiterCalendarRow, incoming: BauleiterCalendarRow): BauleiterCalendarRow {
  return {
    ...incoming,
    projects: [...new Map([...current.projects, ...incoming.projects].map(project => [project.id, project])).values()],
  };
}

interface ProjectBlock {
  start: Dayjs;
  end: Dayjs;
  projects: BauleiterProjectPeriod[];
}

/** Split only at project boundaries and Mondays, without expanding intervals into daily records. */
export function getBauleiterBlocks(projects: BauleiterProjectPeriod[], start: Dayjs, end: Dayjs, zoom: number): ProjectBlock[] {
  const first = start.startOf("day");
  const last = end.startOf("day");
  if (!first.isValid() || !last.isValid() || last < first) return [];

  const firstDate = first.format("YYYY-MM-DD");
  const lastDate = last.format("YYYY-MM-DD");
  const ranges = projects.filter(project => project.plannedStart <= project.plannedEnd
    && project.plannedStart <= lastDate && project.plannedEnd >= firstDate)
    .sort((a, b) => a.number.localeCompare(b.number) || a.id - b.id);
  const boundaries = new Set([firstDate, last.add(1, "day").format("YYYY-MM-DD")]);
  for (const project of ranges) {
    boundaries.add(project.plannedStart > firstDate ? project.plannedStart : firstDate);
    boundaries.add(dayjs(project.plannedEnd < lastDate ? project.plannedEnd : last).add(1, "day").format("YYYY-MM-DD"));
  }
  for (let monday = first.startOf("isoWeek").add(1, "week"); monday <= last; monday = monday.add(1, "week")) {
    boundaries.add(monday.format("YYYY-MM-DD"));
  }

  const dates = [...boundaries].sort();
  const blocks: ProjectBlock[] = [];
  for (let i = 0; i < dates.length - 1; i++) {
    const active = ranges.filter(project => project.plannedStart <= dates[i] && project.plannedEnd >= dates[i]);
    if (!active.length) continue;
    const blockStart = dayjs(dates[i]);
    const blockEnd = dayjs(dates[i + 1]).subtract(1, "day");
    const previous = blocks[blocks.length - 1];
    // Match the other plans' compact mode; keep merged boxes within four calendar weeks.
    if (zoom < 40 && previous && previous.end.add(1, "day").isSame(blockStart, "day")
      && blockEnd.diff(previous.start.startOf("isoWeek"), "day") < 28
      && active.length === previous.projects.length && active.every((project, index) => project.id === previous.projects[index].id)) {
      previous.end = blockEnd;
    } else {
      blocks.push({ start: blockStart, end: blockEnd, projects: active });
    }
  }
  return blocks;
}
